import React, { useCallback, useEffect } from 'react';
import { CssBaseline } from '@material-ui/core';
import { MuiPickersUtilsProvider } from 'material-ui-pickers';
import MomentUtils from '@date-io/moment';

import Header from '../Header';
import Footer from '../Footer';
import Snack from '../Snack';
import styled from 'styled-components';

import './index.scss';
import { NotificationListener } from '../NotificationListener/NotificationListener';
import { setScrollPosition, useGetScrollPosition } from '../../services/scrollPosition/scrollPosititonSlice';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { MobileNavigation } from 'components/mobile/Navigation';
import { useScreen } from 'usehooks-ts';
import { UserGeoProvider } from '../UserGeoProvider/UserGeoProvider';
import GlobalSocketProvider from '../GlobalSocketProvider';
import { ToastBar, Toaster } from 'react-hot-toast';
import { useHideOnScroll } from '../../hooks/useHideOnScroll';
import LinkAccountDialog from '../Header/components/LinkAccount/LinkAccountDialog';
import AllowSmsDialog from '../AllowSmsDialog';
import { useAllowNotificationPopupListener } from '../../hooks/useAllowNotificationPopupListener';
import { useDeviceInfo } from '../../hooks/useDeviceInfo';

class ExtendedMomentUtils extends MomentUtils {
  getWeekdays() {
    return ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  }
}

const LayoutWrapper = styled.div`
  display: ${props => (props.isFlexLayout ? 'flex' : 'block')};

  @media (max-width: 1280px) {
    display: ${props => (props.isFlexMobileLayout ? 'flex' : 'block')};
  }
`;

const Layout = props => {
  const { children } = props;

  const user = useSelector(state => state.user?.user);
  const location = useLocation();

  const isPageWithoutLayout =
    /^\/vehicles\/.*\/(autocheck-report|funding-notice)$/.test(location.pathname) ||
    location.pathname.includes('auction-summary-report/export');

  const isFlexLayout = location.pathname.includes('live-auction');
  const isFlexMobileLayout = location.pathname.startsWith('/lister/upload-vehicle');

  const isLister = user && user.role === 'admin' && user.job_title === 'Lister';

  const { width = 0 } = useScreen();

  const { isMobile } = useDeviceInfo();

  const showNavigation = isMobile && user && !(location.pathname.endsWith('/details') && user.role === 'buyer');
  const isPageWithoutFooter = isMobile || location.pathname.endsWith('/admin/financials/transfer-report');
  const noMarginFooter =
    location.pathname.endsWith('/review') ||
    location.pathname.includes('/admin/financials/transfer-report/document') ||
    location.pathname === '/';
  const hideHeader = isMobile && !user?.id && location.pathname !== '/';

  const scrollPosition = useGetScrollPosition(location.pathname);

  const dispatch = useDispatch();

  const handleScroll = useCallback(() => {
    dispatch(setScrollPosition({ [location.pathname]: window.scrollY }));
  }, [dispatch, location.pathname]);

  useHideOnScroll('mobile-nav', { enabled: isLister && isMobile });

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    window.scrollTo(0, scrollPosition);
  }, [location.pathname]);

  const whiteMobileBackground = (() => {
    if (location.pathname.startsWith('/upload-vehicle')) return true;
    if (location.pathname.startsWith('/lister/upload-vehicle')) return true;
    if (location.pathname.startsWith('/vehicles') && location.pathname.endsWith('/view')) return true;
    if (location.pathname.endsWith('/review')) return true;

    return (
      ['/login', '/register', '/forgot-password', '/success', '/admin/vehicles', '/seller/vehicles'].some(path =>
        location.pathname.startsWith(path)
      ) && isMobile
    );
  })();

  useAllowNotificationPopupListener();

  if (isPageWithoutLayout) {
    return children;
  }

  return (
    <MuiPickersUtilsProvider utils={ExtendedMomentUtils}>
      <Toaster containerStyle={{ zIndex: 99999 }} toasterId="default">
        {t => (
          <ToastBar
            toast={t}
            style={{
              ...t.style
            }}
          />
        )}
      </Toaster>
      <CssBaseline />
      <div
        className={`layout-holder ${
          isPageWithoutFooter &&
          location.pathname !== '/live-auction' &&
          !location.pathname.endsWith('/admin/financials/transfer-report')
            ? 'layout-holder-mobile'
            : ''
        } ${hideHeader ? 'no-header-layout' : ''} ${whiteMobileBackground ? 'white-background' : ''}`}
      >
        <NotificationListener />
        <GlobalSocketProvider />
        <UserGeoProvider />
        {!hideHeader && <Header {...props} />}
        <LayoutWrapper isFlexLayout={isFlexLayout} isFlexMobileLayout={isFlexMobileLayout} className="layout-body">
          {children}
        </LayoutWrapper>
        {!isPageWithoutFooter && <Footer noMargin={noMarginFooter} />}
        <Snack {...props} />
      </div>
      {showNavigation && <MobileNavigation />}
      {user && user.role !== 'admin' && <AllowSmsDialog />}
      {user && user.role !== 'admin' && <LinkAccountDialog />}
    </MuiPickersUtilsProvider>
  );
};

export default Layout;
